/* eslint-disable @nx/enforce-module-boundaries */
import { useState, useRef, useContext, forwardRef, useEffect } from 'react';
import { VerticalThreeDot } from '@mybridge/icons';
import { Avatar, Box, HStack, IconButton, Stack, Text, Checkbox } from '@mybridge/ui';
import { isRootDir, isTeamDir, toPascalCase } from 'v4/components/utils';
import { getProfilePic, getUserFullName } from 'v4/lib/commons';
import { DiskContext } from '../context';
import { FileFolderIcon } from '../my-disk/file-icon';
import { MyDiskItemMenu } from '../my-disk/listing-item-menu';
import { DiskNoFilesIllustration } from '../no-files-illustration';
import { RootDirIcon } from '../my-disk/main-menu/root-dir-icon';
import { isMobile } from 'react-device-detect';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const DiskListingGrid = forwardRef(
  ({ menu, accessType = 'W', accessTypeTeamAccess }, ref) => {
    const {
      files = [],
      filesLoading,
      setSelectedDir,
      selectedFile,
      setSelectedFile,
      setSelectedMainDir,
      userProfileInfo,
      showPreview,
      setShowPreview,
      setSelectedFiles,
    } = useContext(DiskContext);

    const [clickCount, setClickCount] = useState(0);

    // Keep IDs as strings so downstream `.split('_')`
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
      setSelectedIds([]);
    }, [files]);


    const total = files?.length || 0;
    const allSelected = total > 0 && selectedIds.length === total;
    const indeterminate = selectedIds.length > 0 && selectedIds.length < total;

    const toId = (f) => String(f?.id ?? ''); // normalize to string

    // Keep context selection in sync (as strings)
    const syncContextSelection = (ids) => {
      // If you ever need to include type, you can pass `${id}_${type}`.
      setSelectedFiles(ids); // expects strings; mutations will .split('_')
    };

    const toggleSelectAll = (checked) => {
      if (checked) {
        const all = files.map(toId);
        setSelectedIds(all);
        syncContextSelection(all);
      } else {
        setSelectedIds([]);
        syncContextSelection([]);
      }
    };

    const toggleSelectOne = (idStr, checked) => {
      setSelectedIds((prev) => {
        const next = checked ? [...prev, idStr] : prev.filter((id) => id !== idStr);
        syncContextSelection(next);
        return next;
      });
    };

    const tableParentRef = useRef();
    const { t } = useTranslator();

    const onFileSelect = (file) => setSelectedFile(file);

    const handleMobileClick = (file) => {
      setClickCount((c) => c + 1);
      if (clickCount === 1) handleDoubleClick(file);
      else onFileSelect(file);
      setTimeout(() => setClickCount(0), 300);
    };

    const handleDoubleClick = (file) => {
      if (isRootDir(file)) setSelectedMainDir(file);
      setSelectedDir(file);
    };

    return (
      <Stack ref={tableParentRef} height="100%" overflowY="auto">
        {/* Select-all header */}
        {total > 0 && selectedIds?.length && (
          <HStack p={2} alignItems="center">
            <Checkbox
              isChecked={allSelected}
              isIndeterminate={indeterminate}
              onChange={(e) => toggleSelectAll(e.target.checked)}
            />
            <Text>{selectedIds.length} selected</Text>
          </HStack>
        )}

        <HStack spacing={0} gap={2} flexWrap="wrap" w="100%">
          {total > 0 ? (
            <HStack spacing={0} gap={2} flexWrap="wrap" overflowY="auto" maxH="600px" w='full'>
              {files.map((file, idx) => {
                const idStr = toId(file);
                return (
                  <Stack
                    key={idx}
                    _hover={{ bg: 'brandGray.400' }}
                    p={4}
                    w={['47%', '49%', 'auto']}
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="5"
                    cursor="pointer"
                    bg={selectedFile?.id === file?.id ? 'brandGray.500' : ''}
                    onClick={() => (isMobile ? handleMobileClick(file) : onFileSelect(file))}
                    onDoubleClick={() => handleDoubleClick(file)}
                    pos="relative"
                  >
                    {/* Item checkbox */}
                    <Box pos="absolute" left="2" top="2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={selectedIds.includes(idStr)}
                        onChange={(e) => toggleSelectOne(idStr, e.target.checked)}
                      />
                    </Box>

                    {menu ?? (
                      <>
                        {file?.type !== 'RD' && (
                          <Box 
                            pos="absolute" 
                            right="2" 
                            top="2" 
                            onClick={(e) => {
                              e.stopPropagation()
                              onFileSelect(file);
                            }}
                          >
                            <MyDiskItemMenu
                              file={file}
                              isOwner={file?.owner?.id === userProfileInfo?.id}
                              accessType={accessType}
                              permissionsType={file?.access_type}
                              teamDetails={file?.team_ref ? file?.team_info : null}
                            >
                              <IconButton variant="ghost">
                                <VerticalThreeDot width="16" height="16" />
                              </IconButton>
                            </MyDiskItemMenu>
                          </Box>
                        )}
                      </>
                    )}

                    <Stack onClick={() => setShowPreview(true)} justifyContent="center" alignItems="center">
                      {isRootDir(file) ? (
                        <RootDirIcon dir={file} width="48" height="48" />
                      ) : isTeamDir(file) ? (
                        <Avatar size="lg" src={getProfilePic(file?.team_info)} name={file?.team_info?.name} />
                      ) : (
                        <FileFolderIcon file={file} width="48" height="48" />
                      )}
                      <Box>
                        <Text
                          textAlign="center"
                          title={file?.name}
                          w="150px"
                          overflow="hidden"
                          whiteSpace="nowrap"
                          textOverflow="ellipsis"
                        >
                          {isRootDir(file)
                            ? toPascalCase(file?.name)
                            : isTeamDir(file)
                            ? file?.team_info?.name
                            : file?.name || getUserFullName(file) || file?.file?.name}
                        </Text>
                        {file?.type === 'D' && file?.file_count > 0 && (
                          <Text
                            textAlign="center"
                            w="150px"
                            color="brandPrimary.500"
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                          >
                            {`${file?.file_count} ${
                              file?.file_count > 1 ? t('pageJobs.files') || 'files' : t('otherModules.file') || 'file'
                            }`}
                          </Text>
                        )}
                      </Box>
                    </Stack>
                  </Stack>
                );
              })}
            </HStack>
          ) : (
            <DiskNoFilesIllustration loading={filesLoading} />
          )}
        </HStack>
      </Stack>
    );
  }
);
